import { parseYearMonthKey } from './year-month-labels';

const RANGE_FILTER_ITEM = 'rangeFilterSaleDate';
const YEAR_MONTH_COMBO_ITEM = 'comboBoxYearMonth';

function parseDashboardState(stateString) {
    if (!stateString) {
        return { Items: {} };
    }
    try {
        const parsed = JSON.parse(stateString);
        if (!parsed.Items) {
            parsed.Items = {};
        }
        return parsed;
    } catch {
        return { Items: {} };
    }
}

function cloneDashboardState(state) {
    return JSON.parse(JSON.stringify(state));
}

function getMonthFilterValues(state) {
    return state?.Items?.[YEAR_MONTH_COMBO_ITEM]?.MasterFilterValues;
}

function getRangeFilterState(state) {
    return state?.Items?.[RANGE_FILTER_ITEM]?.RangeFilterState;
}

function getRangeSelection(state) {
    return getRangeFilterState(state)?.Selection;
}

function hasMonthFilter(values) {
    if (!Array.isArray(values) || values.length === 0) {
        return false;
    }
    return values.some((row) => Array.isArray(row) && row.length > 0 && row[0] != null && row[0] !== '');
}

function normalizeRangeBounds(selection) {
    if (!selection) {
        return null;
    }
    const minimum = selection.Minimum ?? selection.minimum;
    const maximum = selection.Maximum ?? selection.maximum;
    if (minimum == null || maximum == null) {
        return null;
    }
    return { Minimum: minimum, Maximum: maximum };
}

function hasRangeFilter(selection) {
    return normalizeRangeBounds(selection) != null;
}

function formatDashboardDate(date) {
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.000`;
}

/** First/last instant of a calendar month for rangeFilterSaleDate (SaleDateTime). */
export function yearMonthToRangeBounds(yearMonth) {
    const match = /^(\d{4})-(\d{2})$/.exec(String(yearMonth ?? '').trim());
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return null;
    }
    const minimum = new Date(year, month - 1, 1, 0, 0, 0, 0);
    const maximum = new Date(year, month, 0, 23, 59, 59, 999);
    return {
        Minimum: formatDashboardDate(minimum),
        Maximum: formatDashboardDate(maximum)
    };
}

function getSelectedYearMonth(state) {
    const values = getMonthFilterValues(state);
    if (!hasMonthFilter(values)) {
        return null;
    }
    return parseYearMonthKey(values[0][0]);
}

function getLocalYearMonth(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function rangeMatchesYearMonth(bounds, yearMonth) {
    const expected = yearMonthToRangeBounds(yearMonth);
    if (!expected || !bounds) {
        return false;
    }
    const min = new Date(bounds.Minimum);
    const max = new Date(bounds.Maximum);
    const expectedMin = new Date(expected.Minimum);
    const expectedMax = new Date(expected.Maximum);
    if (Number.isNaN(min.getTime()) || Number.isNaN(max.getTime())) {
        return false;
    }
    return getLocalYearMonth(min) === yearMonth
        && getLocalYearMonth(max) === yearMonth
        && Math.abs(min.getTime() - expectedMin.getTime()) < 36 * 60 * 60 * 1000
        && Math.abs(max.getTime() - expectedMax.getTime()) < 36 * 60 * 60 * 1000;
}

function serializeMonthFilter(values) {
    return JSON.stringify(values ?? []);
}

function serializeRangeFilter(selection) {
    const bounds = normalizeRangeBounds(selection);
    if (!bounds) {
        return 'null';
    }
    const min = new Date(bounds.Minimum);
    const max = new Date(bounds.Maximum);
    if (Number.isNaN(min.getTime()) || Number.isNaN(max.getTime())) {
        return JSON.stringify(bounds);
    }
    return JSON.stringify({
        minDay: min.toISOString().slice(0, 10),
        maxDay: max.toISOString().slice(0, 10)
    });
}

function clearRangeFilter(state) {
    const next = cloneDashboardState(state);
    if (!next.Items[RANGE_FILTER_ITEM]) {
        return next;
    }
    delete next.Items[RANGE_FILTER_ITEM].RangeFilterState;
    if (Object.keys(next.Items[RANGE_FILTER_ITEM]).length === 0) {
        delete next.Items[RANGE_FILTER_ITEM];
    }
    return next;
}

function clearMonthFilter(state) {
    const next = cloneDashboardState(state);
    if (!next.Items[YEAR_MONTH_COMBO_ITEM]) {
        return next;
    }
    delete next.Items[YEAR_MONTH_COMBO_ITEM].MasterFilterValues;
    if (Object.keys(next.Items[YEAR_MONTH_COMBO_ITEM]).length === 0) {
        delete next.Items[YEAR_MONTH_COMBO_ITEM];
    }
    return next;
}

/** Month combo + sale-date range aligned to that month. */
export function buildMonthAndRangeState(yearMonth, existingState = { Items: {} }) {
    const bounds = yearMonthToRangeBounds(yearMonth);
    const next = cloneDashboardState(existingState);
    if (!bounds) {
        return next;
    }
    next.Items[YEAR_MONTH_COMBO_ITEM] = {
        MasterFilterValues: [[yearMonth]]
    };
    next.Items[RANGE_FILTER_ITEM] = {
        RangeFilterState: {
            Selection: bounds
        }
    };
    return next;
}

/** Custom sale-date range only (month combo cleared). */
export function buildCustomRangeState(rangeSelection, existingState = { Items: {} }) {
    const next = clearMonthFilter(cloneDashboardState(existingState));
    const bounds = normalizeRangeBounds(rangeSelection);
    if (bounds) {
        next.Items[RANGE_FILTER_ITEM] = {
            RangeFilterState: {
                Selection: bounds
            }
        };
    }
    return next;
}

export function buildYearMonthOnlyState(yearMonth) {
    return buildMonthAndRangeState(yearMonth);
}

export function buildRangeOnlyState(rangeSelection, existingState = { Items: {} }) {
    return buildCustomRangeState(rangeSelection, existingState);
}

/**
 * Month combo drives sale-date range; custom ranges clear the month combo.
 * Returns a new state when a change is applied, otherwise null.
 */
export function reconcilePeriodFilters(previousState, nextState) {
    const selectedYearMonth = getSelectedYearMonth(nextState);
    const nextRangeBounds = normalizeRangeBounds(getRangeSelection(nextState));

    if (selectedYearMonth && !nextRangeBounds) {
        return buildMonthAndRangeState(selectedYearMonth, nextState);
    }

    const previousMonth = serializeMonthFilter(getMonthFilterValues(previousState));
    const nextMonth = serializeMonthFilter(getMonthFilterValues(nextState));
    const previousRange = serializeRangeFilter(getRangeSelection(previousState));
    const nextRange = serializeRangeFilter(getRangeSelection(nextState));

    const monthChanged = previousMonth !== nextMonth;
    const rangeChanged = previousRange !== nextRange;

    if (monthChanged) {
        if (selectedYearMonth) {
            return buildMonthAndRangeState(selectedYearMonth, nextState);
        }
        return clearRangeFilter(clearMonthFilter(nextState));
    }

    if (rangeChanged && nextRangeBounds) {
        if (selectedYearMonth && rangeMatchesYearMonth(nextRangeBounds, selectedYearMonth)) {
            return null;
        }
        return buildCustomRangeState(nextRangeBounds, nextState);
    }

    return null;
}

export function isPeriodPickerDialogOpen() {
    if (typeof document === 'undefined') {
        return false;
    }
    return [...document.querySelectorAll('.dx-popup-wrapper .dx-popup-title')].some(
        (el) => el.textContent?.trim() === 'Velg periode'
    );
}

export function readRangeSelectionFromViewer(dashboardControl) {
    const viewerApi = getViewerApi(dashboardControl);
    if (!viewerApi) {
        return null;
    }
    try {
        const range = viewerApi.getCurrentRange(RANGE_FILTER_ITEM);
        return normalizeRangeBounds(range);
    } catch {
        return null;
    }
}

function getViewerApi(dashboardControl) {
    if (!dashboardControl?.findExtension) {
        return null;
    }
    return dashboardControl.findExtension('viewer-api');
}

export function applyPeriodFiltersToViewer(dashboardControl, state) {
    return state;
}

export function applyExclusivePeriodFiltersToViewer(dashboardControl, state) {
    return applyPeriodFiltersToViewer(dashboardControl, state);
}

export function hasActivePeriodConflict(state) {
    const yearMonth = getSelectedYearMonth(state);
    const rangeBounds = normalizeRangeBounds(getRangeSelection(state));
    if (!yearMonth || !rangeBounds) {
        return false;
    }
    return !rangeMatchesYearMonth(rangeBounds, yearMonth);
}

export {
    RANGE_FILTER_ITEM,
    YEAR_MONTH_COMBO_ITEM,
    parseDashboardState,
    hasMonthFilter,
    hasRangeFilter
};
